import { Badge } from '@/ui/Badge';
import { cn } from '@/lib/cn';
import {
  diasDeRango, esCerrada, ESTADO_TXT, ESTADO_TONO,
  type Adjunto, type Aprobacion, type PasoDelFlujo, type ValorDeCampo,
} from './api';

/**
 * Piezas que comparten «Mis solicitudes», «Por aprobar» y «Bandeja».
 *
 * Están juntas porque las tres pintan lo mismo con distinto encuadre, y tenerlas
 * en cada archivo garantizaba que una se quedara atrás: ya pasó con el nombre
 * del solicitante, que `_inboxTabla()` escapaba y `_pending()` no.
 */

/** La insignia de estado.
 *
 *  Los colores viven en los tonos del sistema y ya no en un `style` incrustado.
 *  El patrón anterior pintaba el texto con EL MISMO color del fondo tintado
 *  (`background:${c}22; color:${c}`), y en modo claro eso daba **1,85 de
 *  contraste** en «Pendiente», «Aprobada» y «Rechazada» — muy por debajo del 4,5
 *  que exige un texto de 11 px. Al ser estilo incrustado, además, no había forma
 *  de corregirlo en modo oscuro.
 *
 *  Dos estados pueden compartir tono —«Aprobada» y «Finalizada» ya lo hacían—;
 *  lo que los distingue es la palabra, nunca solo el color. */
export function EstadoBadge({ estado }: { estado: string }) {
  return <Badge tone={ESTADO_TONO[estado] ?? 'neutral'}>{ESTADO_TXT[estado] ?? estado}</Badge>;
}

/**
 * Un valor de `form_data`.
 *
 * Lo escribió el solicitante en un campo libre y el servidor no lo sanea
 * —`_validar_contenido()` comprueba longitud, formato y `pattern`, nada de
 * marcado—, así que llega crudo. En la versión vanilla esto era `_fmtVal()` y
 * tenía que escapar a mano cada rama; aquí lo hace JSX.
 */
export function ValorDeFormulario({ valor }: { valor: ValorDeCampo }) {
  if (Array.isArray(valor)) return <>{valor.join(', ')}</>;
  // El período se guarda como {inicio, fin}. Sin esta rama, la ficha del
  // colaborador y la pantalla del aprobador mostraban «[object Object]» justo
  // donde el jefe necesita leer qué días le están pidiendo.
  if (valor && typeof valor === 'object') {
    const d = diasDeRango(valor.inicio, valor.fin);
    return (
      <>
        {valor.inicio} a {valor.fin}
        {d != null && <strong> ({d} día{d === 1 ? '' : 's'})</strong>}
      </>
    );
  }
  if (valor === '' || valor == null) return <>—</>;
  return <>{String(valor)}</>;
}

export function FlujoDePasos({
  flujo, pasoActual, estado,
}: { flujo?: PasoDelFlujo[]; pasoActual: number; estado: string }) {
  if (!flujo?.length) return <p className="text-3 text-muted">Sin flujo</p>;
  const cerrada = esCerrada(estado);
  return (
    <ol className="flex flex-col gap-1.5">
      {flujo.map((s, i) => {
        const hecho = i < pasoActual || estado === 'finalizada';
        const activo = i === pasoActual && !cerrada;
        return (
          <li key={i} className="flex items-center gap-2 text-3">
            <i
              className={cn('bi shrink-0',
                hecho ? 'bi-check-circle-fill text-ok-ink'
                  : activo ? 'bi-arrow-right-circle text-brand-ink'
                  : 'bi-circle text-muted')}
              aria-hidden="true"
            />
            <span className={cn(hecho || activo ? 'text-ink' : 'text-muted',
                                activo && 'font-semibold')}>
              {s.label}
            </span>
            {/* El estado va también en palabra: el icono y el color solos no lo
                dicen a quien no distingue tonos. */}
            <span className="sr-only">
              {hecho ? '(paso completado)' : activo ? '(paso actual)' : '(paso pendiente)'}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Quién firmó cada paso. Con jefes suplentes ya no se deduce del flujo, porque
 *  cualquiera de varios pudo aprobar. */
export function Historial({ lista }: { lista?: Aprobacion[] }) {
  if (!lista?.length) return null;
  return (
    <section>
      <h3 className="mb-2 text-2 font-semibold uppercase tracking-wide text-muted">Historial</h3>
      <ul className="rounded-sm border border-line bg-surface">
        {lista.map((a, i) => {
          const negativa = a.decision !== 'aprobado';
          return (
            <li key={i} className="border-b border-line px-4 py-2.5 last:border-0">
              <p className="flex items-start gap-2 text-3 text-ink">
                <i className={cn('bi mt-0.5 shrink-0',
                  negativa ? 'bi-x-circle-fill text-danger-ink' : 'bi-check-circle-fill text-ok-ink')}
                   aria-hidden="true" />
                <span>
                  <span className="sr-only">
                    {a.decision === 'aprobado' ? 'Aprobado' : a.decision === 'devuelto' ? 'Devuelto' : 'Rechazado'}:{' '}
                  </span>
                  {a.step_label}
                  {a.note && <> — <em className="text-ink-2">{a.note}</em></>}
                </span>
              </p>
              <p className="mt-0.5 pl-6 text-2 text-muted">
                por <strong>{a.approver_name || '—'}</strong>
                {a.created_at && <> · {Utils.fmtDate(a.created_at)}</>}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Los adjuntos.
 *
 * `filename` lo elige quien sube el archivo y el servidor lo guarda **crudo**
 * (`solicitudes.py:1110` conserva `file.filename`; el saneado de la línea 1106
 * solo se usa para la clave de almacenamiento). La versión vanilla lo
 * interpolaba sin escapar en el `innerHTML` del detalle, que es la pantalla que
 * abre el aprobador: un nombre de archivo con marcado se ejecutaba en la
 * pantalla del jefe. `test_escapado.py` no lo veía porque `.filename` no está en
 * su lista de nombres.
 */
export function Adjuntos({ lista }: { lista?: Adjunto[] }) {
  if (!lista?.length) return null;
  return (
    <section>
      <h3 className="mb-2 text-2 font-semibold uppercase tracking-wide text-muted">Adjuntos</h3>
      <ul className="flex flex-wrap gap-2">
        {lista.map((a, i) => (
          <li key={i}>
            <a
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-8 max-w-72 items-center gap-2 rounded-sm border border-line
                         bg-surface px-3 text-2 font-medium text-ink-2 transition-colors
                         hover:border-brand-ink hover:text-brand-ink
                         focus-visible:focus-anillo
                         pointer-coarse:min-h-11"
            >
              <i className="bi bi-paperclip shrink-0" aria-hidden="true" />
              <span className="truncate">{a.filename}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
